<script setup lang="ts">
import type { FormInstance } from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form';

import { ref, nextTick, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { comApi } from '@/http/api';
import commissioningApiClient from '@/http/commissioningApiClient';
import jwt_decode from 'jwt-decode';
import ButtonPrimary from '@shared/components/button/ButtonPrimary.vue';
import BaseForm from '@/shared/components/BaseForm.vue';

import { useAuthStore } from '@/shared/stores/authStore';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { isCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { isCognitoLocalEmulator } from '@/shared/auth/cognitoLocalEmulator';
import { validateIsEmail } from '@/utils/validators';
import { resolveHttpErrorMessage } from '@/http/http-error';
import { usePasswordRecovery } from '@/shared/auth/usePasswordRecovery';
import { stopPollings } from '../utils/init-pollings';

import ComModal from '../components/ComModal.vue';
import ComRegisterForm from '../components/ComRegisterForm.vue';
import { ComRegistrationSource } from '../models/ComRegistrationForm';

interface LoginJwtPayload {
  exp: number;
  email: string;
  role: string[];
}

interface FormState {
  email: string;
  password: string;
}

const form = ref<FormInstance>();
const loginLoading = ref(false);
const forgetPasswordModalVisible = ref(false);
const registerModalVisible = ref(false);
const forgetPasswordEmail = ref('');
const sendLoading = ref(false);
const recoveryAvailable = usePasswordRecovery();
const formData = ref<FormState>({ email: '', password: '' });

const router = useRouter();
const authStore = useAuthStore();

const validateMessages: Record<string, Rule[]> = {
  password: [
    {
      type: 'string',
      required: true,
      message: 'Please input password',
      trigger: 'blur',
    },
  ],
  email: [
    {
      type: 'email',
      required: true,
      message: 'Please input a valid email address',
      trigger: 'blur',
    },
  ],
};

onMounted(() => {
  stopPollings();
});

const forgetPasswordInput = ref<{ focus?: () => void } | null>(null);
const registerFormRef = ref<{ focusFirstInput?: () => void; resetForm?: () => void } | null>(null);

const blurActiveElement = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }
};

const focusForgetPasswordInput = async () => {
  await nextTick();
  forgetPasswordInput.value?.focus?.();
};

const forgetPasswordClicked = async () => {
  forgetPasswordEmail.value = formData.value.email || '';
  blurActiveElement();
  forgetPasswordModalVisible.value = true;
  await focusForgetPasswordInput();
};

const forgetPasswordSend = async () => {
  if (!recoveryAvailable.value || sendLoading.value) return;
  if (!forgetPasswordEmail.value || !validateIsEmail(forgetPasswordEmail.value)) {
    message.error('Please enter a valid email address');
    return;
  }

  sendLoading.value = true;
  try {
    await commissioningApiClient.post(comApi.forgetPassword, {
      email: forgetPasswordEmail.value,
      password: '',
      source: ComRegistrationSource.ComApp2,
    });
    forgetPasswordModalVisible.value = false;
    message.success('If your account can be recovered, you will receive a password reset email.');
  } catch (error) {
    message.error('Unable to request a password reset. Please try again later.');
  } finally {
    sendLoading.value = false;
  }
};

const loginClicked = async () => {
  await form.value?.validate();

  try {
    loginLoading.value = true;
    const payload = {
      ...formData.value,
      source: ComRegistrationSource.ComApp2,
    };
    const {
      data: { token },
    } = await commissioningApiClient.post(comApi.login, payload);

    const { exp, email, role } = jwt_decode(token) as LoginJwtPayload;
    authStore.setMainServerComLoginAuth({ token, exp, email, role });
    await router.push('/commissioning/welcome');
  } catch (error) {
    const errorMessage = resolveHttpErrorMessage(error, 'Email or password is incorrect.');
    message.error(errorMessage);
  } finally {
    loginLoading.value = false;
  }
};

const focusRegisterInput = async () => {
  await nextTick();
  registerFormRef.value?.focusFirstInput?.();
};

const registerClicked = async () => {
  blurActiveElement();
  registerModalVisible.value = true;
  await focusRegisterInput();
};

const handleRegisterSuccess = (email: string, password: string) => {
  formData.value.email = email;
  formData.value.password = password;
  registerModalVisible.value = false;
};

watch(registerModalVisible, (isVisible) => {
  if (isVisible) {
    registerFormRef.value?.resetForm?.();
  }
});

const cognitoLoginClicked = async () => {
  try {
    await cognitoClient.login('/commissioning/welcome');
  } catch (error) {
    console.error('Cognito login failed:', error);
    message.error('Could not start Cognito sign-in. Please try again.');
  }
};
</script>

<template>
  <main class="content">
    <h3>Login to access the SolShare commissioning app.</h3>

    <BaseForm class="login-form" ref="form" :model="formData" :rules="validateMessages">
      <a-form-item label="Email" name="email">
        <a-input
          v-model:value="formData.email"
          placeholder="Email"
          size="large"
          data-tn="login-email-input"
        />
      </a-form-item>
      <a-form-item label="Password" name="password" class="password-form-item">
        <a-input-password
          v-model:value="formData.password"
          placeholder="Password"
          data-tn="login-password-input"
          size="large"
          @keydown.enter="loginClicked"
        />
      </a-form-item>
      <div class="link-container">
        <div class="link-text" data-tn="register-link" @click="registerClicked">
          Don't have an account? Register
        </div>
        <div class="link-text" data-tn="forget-password-link" @click="forgetPasswordClicked">
          Need to reset your password?
        </div>
      </div>
    </BaseForm>

    <ButtonPrimary block :loading="loginLoading" data-tn="login-submit" @click="loginClicked">
      Login
    </ButtonPrimary>

    <template v-if="isCognitoEnabled">
      <div class="cognito-login-block">
        <a-divider plain>or</a-divider>
        <ButtonPrimary block data-tn="login-cognito-submit" @click="cognitoLoginClicked">
          Login or Sign Up via Cognito
        </ButtonPrimary>
        <p v-if="isCognitoLocalEmulator()" class="cognito-dev-hint">
          Local dev: you will be redirected to cognito-local's plain sign-in page (port 8005).
        </p>
      </div>
    </template>

    <ComModal
      v-model:visible="forgetPasswordModalVisible"
      title="Forgotten password?"
      description="We will send you a link you can use to create a new password."
      data-tn="forget-password-modal"
    >
      <p v-if="!recoveryAvailable" role="status">
        Please contact Allume support to restore access to your account.
      </p>
      <div v-else class="form-section">
        <label class="form-label">Email</label>
        <a-input
          ref="forgetPasswordInput"
          v-model:value="forgetPasswordEmail"
          placeholder="Email address"
          size="large"
          class="rounded-input"
          data-tn="forget-password-email-input"
          @keydown.enter="forgetPasswordSend"
        />
      </div>
      <template #modal-buttons>
        <ButtonPrimary
          v-if="recoveryAvailable"
          class="send-button"
          block
          :loading="sendLoading"
          @click="forgetPasswordSend"
          data-tn="forget-password-send-button"
        >
          Send
        </ButtonPrimary>
      </template>
    </ComModal>

    <ComModal
      v-model:visible="registerModalVisible"
      :modal-top="0"
      modal-body-max-height="90vh"
      title="Register a new account"
      description="Create an account to access SolShare commissioning app."
      data-tn="register-modal"
    >
      <ComRegisterForm ref="registerFormRef" @success="handleRegisterSuccess" />
    </ComModal>
  </main>
</template>

<style lang="less" scoped>
@import '../../../assets/style/variables.less';

.login-form {
  padding: 8px 0;
  width: 100%;
  margin-bottom: 1rem;
}

.password-form-item {
  margin-bottom: 0.5rem;
}

.cognito-login-block {
  width: 100%;
  margin-top: 1rem;

  .cognito-dev-hint {
    margin-top: 8px;
    font-size: smaller;
    color: @color-secondary;
    line-height: 1.4;
  }

  .register-link {
    margin-top: 12px;
  }
}

.link-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 20px;

  .link-text {
    flex: 1 1 60%;
    cursor: pointer;
    text-align: left;
    color: @color-secondary;
  }

  .link-text:last-child {
    flex: 0 0 auto;
  }
}

.form-section {
  margin-bottom: 24px;

  .form-label {
    display: block;
    color: @color-primary;
    font-weight: @font-weight-medium;
    margin-bottom: 8px;
  }

  .rounded-input {
    border-radius: @border-radius-medium;
    border-color: @color-primary;
  }
}
</style>
